import React, { useEffect, useState } from 'react';
import { useTransition } from '@context/TransitionContext';
import { useAnimationContext } from '@context/AnimationContext';

/**
 * TransitionLoader - Displays a full-screen logo animation during page transitions
 * Creates a branded interstitial between page navigation with smooth, fluid transitions
 */
const TransitionLoader: React.FC = () => {
  const { isTransitioning } = useTransition();
  const { animationsEnabled } = useAnimationContext();
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let animFrame1: number;
    let animFrame2: number;
    let timer: NodeJS.Timeout;

    if (isTransitioning) {
      setMounted(true);
      // Use double requestAnimationFrame to ensure the browser has painted the initial mounted state (opacity: 0, scale: 0.92) before transitioning to active state
      animFrame1 = requestAnimationFrame(() => {
        animFrame2 = requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      // Unmount after the longest transition (600ms for logo transform) completes
      timer = setTimeout(() => setMounted(false), 600);
    }

    return () => {
      if (animFrame1) cancelAnimationFrame(animFrame1);
      if (animFrame2) cancelAnimationFrame(animFrame2);
      if (timer) clearTimeout(timer);
    };
  }, [isTransitioning]);

  if (!mounted) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-background"
      style={{
        opacity: visible ? 1 : 0,
        backdropFilter: animationsEnabled && visible ? 'blur(8px)' : 'none',
        transition: animationsEnabled
          ? 'opacity 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96)'
          : 'none',
      }}
    >
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1) translateY(0px)' : 'scale(0.92) translateY(8px)',
          transition: animationsEnabled
            ? 'opacity 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96), transform 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96)'
            : 'none',
        }}
      >
        <img
          src={`${import.meta.env.BASE_URL}assets/images/logo/sscottlogo.png`}
          alt="Shaun Scott Logo"
          className="h-12 w-auto"
        />
      </div>
    </div>
  );
};

export default TransitionLoader;
